/**
 * Filter Store for managing hierarchical filters.
 *
 * Implements the three-level filter hierarchy:
 * 1. Global Filters - Header bar, apply to ALL pages/modules via subject master
 * 2. Local Filters - Page-specific, PERSISTS across navigation
 * 3. Module Filters - Column-level, resets on navigation
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// =============================================================================
// Types
// =============================================================================

/**
 * Filter value can be single value or array for multi-select.
 */
export type FilterValue = string | number | string[] | number[] | { start?: string; end?: string } | null

/**
 * Global filter configuration from backend.
 */
export interface GlobalFilterConfig {
  id: string
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search' | 'date_range'
  sourceColumn: string
  sortOrder: number
  isEnabled: boolean
  cachedValues?: string[]
}

/**
 * Local filter configuration for a page.
 */
export interface LocalFilterConfig {
  id: string
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search'
  sourceDataset: string
  sourceColumn: string
  sortOrder: number
  isEnabled: boolean
  defaultValues?: string[]
}

/**
 * Study filter configuration from backend.
 */
export interface StudyFilterConfig {
  id: string
  studyId: string
  subjectMasterDataset: string
  subjectIdColumn: string
  globalFilters: GlobalFilterConfig[]
  datasetLinks: Array<{
    id: string
    datasetName: string
    subjectIdColumn: string
  }>
}

/**
 * Saved filter definition.
 */
export interface SavedFilter {
  id: string
  name: string
  description?: string
  filterDefinition: {
    globalFilters: Record<string, FilterValue>
    localFilters: Record<string, Record<string, FilterValue>>
  }
  pageKey?: string
  isShared: boolean
  isDefault: boolean
  createdBy: string
  createdAt: string
}

// =============================================================================
// Store State
// =============================================================================

interface FilterState {
  // Configuration
  filterConfig: StudyFilterConfig | null
  pageFilterConfigs: Record<string, LocalFilterConfig[]> // keyed by pageKey

  // Global filters - apply to ALL pages/modules
  globalFilters: Record<string, FilterValue>

  // Local filters - keyed by pageKey, PERSISTS across navigation
  localFilters: Record<string, Record<string, FilterValue>>

  // Module filters - keyed by moduleInstanceId, resets on navigation
  moduleFilters: Record<string, Record<string, FilterValue>>

  // Saved filters
  savedFilters: SavedFilter[]

  // Cached filter values for dropdowns
  cachedGlobalValues: Record<string, string[]>
  cachedLocalValues: Record<string, Record<string, string[]>> // pageKey -> filterKey -> values

  // Loading states
  isLoadingConfig: boolean
  isLoadingValues: boolean

  // Current page key (for local filter context)
  currentPageKey: string | null

  // Compare mode - per-table, keyed by instanceId, persists across navigation
  compareModes: Record<string, boolean>

  // Compare version - which comparison version to use (study-wide)
  compareVersion: string

  // SMQ filter - for AE page filtering via SMQ terms
  smqFilter: { smqNames: string[]; scope: 'Narrow' | 'Broad' | 'Both' } | null
}

interface FilterActions {
  // Configuration
  setFilterConfig: (config: StudyFilterConfig | null) => void
  setPageFilterConfig: (pageKey: string, configs: LocalFilterConfig[]) => void

  // Global filters
  setGlobalFilter: (key: string, value: FilterValue) => void
  resetGlobalFilters: () => void

  // Local filters
  setLocalFilter: (pageKey: string, filterKey: string, value: FilterValue) => void
  resetLocalFilters: (pageKey: string) => void

  // Module filters
  setModuleFilter: (moduleId: string, column: string, value: FilterValue) => void
  resetModuleFilters: (moduleId: string) => void

  // Reset all filters
  resetAllFilters: () => void

  // Page navigation
  setCurrentPageKey: (pageKey: string | null) => void
  onPageNavigate: (newPageKey: string) => void

  // Saved filters
  setSavedFilters: (filters: SavedFilter[]) => void
  applySavedFilter: (filter: SavedFilter) => void

  // Cached values
  setCachedGlobalValues: (values: Record<string, string[]>) => void
  setCachedLocalValues: (pageKey: string, values: Record<string, string[]>) => void

  // Loading states
  setLoadingConfig: (loading: boolean) => void
  setLoadingValues: (loading: boolean) => void

  // Compare mode (per-table)
  setCompareMode: (instanceId: string, enabled: boolean) => void
  setCompareVersion: (version: string) => void

  // SMQ filter
  setSmqFilter: (filter: { smqNames: string[]; scope: 'Narrow' | 'Broad' | 'Both' }) => void
  clearSmqFilter: () => void

  // Utility
  getActiveGlobalFilters: () => Record<string, FilterValue>
  getActiveLocalFilters: (pageKey: string) => Record<string, FilterValue>
  hasActiveFilters: () => boolean
}

type FilterStore = FilterState & FilterActions

// =============================================================================
// Store Implementation
// =============================================================================

export const useFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
  // Initial state
  filterConfig: null,
  pageFilterConfigs: {},
  globalFilters: {},
  localFilters: {},
  moduleFilters: {},
  savedFilters: [],
  cachedGlobalValues: {},
  cachedLocalValues: {},
  isLoadingConfig: false,
  isLoadingValues: false,
  currentPageKey: null,
  compareModes: {},
  compareVersion: 'vs_previous',
  smqFilter: null,

  // Configuration
  setFilterConfig: (config) => {
    set({
      filterConfig: config,
      // Populate cached global values from config
      cachedGlobalValues: config
        ? config.globalFilters.reduce((acc, gf) => {
            if (gf.cachedValues) {
              acc[gf.filterKey] = gf.cachedValues
            }
            return acc
          }, {} as Record<string, string[]>)
        : {},
    })
  },

  setPageFilterConfig: (pageKey, configs) => {
    set((state) => ({
      pageFilterConfigs: {
        ...state.pageFilterConfigs,
        [pageKey]: configs,
      },
    }))
  },

  // Global filters
  setGlobalFilter: (key, value) => {
    set((state) => ({
      globalFilters: {
        ...state.globalFilters,
        [key]: value,
      },
    }))
  },

  resetGlobalFilters: () => {
    set({ globalFilters: {} })
  },

  // Local filters
  setLocalFilter: (pageKey, filterKey, value) => {
    set((state) => {
      const pageFilters = state.localFilters[pageKey] || {}
      return {
        localFilters: {
          ...state.localFilters,
          [pageKey]: {
            ...pageFilters,
            [filterKey]: value,
          },
        },
      }
    })
  },

  resetLocalFilters: (pageKey) => {
    set((state) => {
      const updated = { ...state.localFilters }
      delete updated[pageKey]
      return { localFilters: updated }
    })
  },

  // Module filters
  setModuleFilter: (moduleId, column, value) => {
    set((state) => {
      const moduleFilters = state.moduleFilters[moduleId] || {}
      return {
        moduleFilters: {
          ...state.moduleFilters,
          [moduleId]: {
            ...moduleFilters,
            [column]: value,
          },
        },
      }
    })
  },

  resetModuleFilters: (moduleId) => {
    set((state) => {
      const updated = { ...state.moduleFilters }
      delete updated[moduleId]
      return { moduleFilters: updated }
    })
  },

  // Reset all filters
  resetAllFilters: () => {
    set({
      globalFilters: {},
      localFilters: {},
      moduleFilters: {},
    })
  },

  // Page navigation
  setCurrentPageKey: (pageKey) => {
    set({ currentPageKey: pageKey })
  },

  onPageNavigate: (newPageKey) => {
    // Module filters reset on navigation
    set({
      currentPageKey: newPageKey,
      moduleFilters: {},
    })
  },

  // Saved filters
  setSavedFilters: (filters) => {
    set({ savedFilters: filters })
  },

  applySavedFilter: (filter) => {
    set({
      globalFilters: filter.filterDefinition.globalFilters || {},
      localFilters: filter.filterDefinition.localFilters || {},
    })
  },

  // Cached values
  setCachedGlobalValues: (values) => {
    set({ cachedGlobalValues: values })
  },

  setCachedLocalValues: (pageKey, values) => {
    set((state) => ({
      cachedLocalValues: {
        ...state.cachedLocalValues,
        [pageKey]: values,
      },
    }))
  },

  // Loading states
  setLoadingConfig: (loading) => {
    set({ isLoadingConfig: loading })
  },

  setLoadingValues: (loading) => {
    set({ isLoadingValues: loading })
  },

  // Compare mode (per-table)
  setCompareMode: (instanceId, enabled) => {
    set((state) => ({
      compareModes: {
        ...state.compareModes,
        [instanceId]: enabled,
      },
    }))
  },

  setCompareVersion: (version) => {
    set({ compareVersion: version })
  },

  // SMQ filter
  setSmqFilter: (filter) => {
    set({ smqFilter: filter })
  },

  clearSmqFilter: () => {
    set({ smqFilter: null })
  },

  // Utility
  getActiveGlobalFilters: () => {
    const { globalFilters } = get()
    return Object.entries(globalFilters).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '' &&
          !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value
      }
      return acc
    }, {} as Record<string, FilterValue>)
  },

  getActiveLocalFilters: (pageKey) => {
    const { localFilters } = get()
    const pageFilters = localFilters[pageKey] || {}
    return Object.entries(pageFilters).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '' &&
          !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value
      }
      return acc
    }, {} as Record<string, FilterValue>)
  },

  hasActiveFilters: () => {
    const { globalFilters, localFilters, moduleFilters } = get()

    const hasGlobal = Object.values(globalFilters).some(
      v => v !== null && v !== undefined && v !== '' &&
           !(Array.isArray(v) && v.length === 0)
    )

    const hasLocal = Object.values(localFilters).some(
      page => Object.values(page).some(
        v => v !== null && v !== undefined && v !== '' &&
             !(Array.isArray(v) && v.length === 0)
      )
    )

    const hasModule = Object.values(moduleFilters).some(
      module => Object.values(module).some(
        v => v !== null && v !== undefined && v !== '' &&
             !(Array.isArray(v) && v.length === 0)
      )
    )

    return hasGlobal || hasLocal || hasModule
  },
}),
    {
      name: 'sage-filters',
      storage: createJSONStorage(() => sessionStorage),
      // Only persist filter values and compare mode, not config/loading states
      partialize: (state) => ({
        globalFilters: state.globalFilters,
        localFilters: state.localFilters,
        compareModes: state.compareModes,
        compareVersion: state.compareVersion,
        smqFilter: state.smqFilter,
      }),
    }
  )
)

// =============================================================================
// Selectors
// =============================================================================

/**
 * Get the filter configuration for the current study.
 */
export const useFilterConfig = () => useFilterStore((state) => state.filterConfig)

/**
 * Get global filter values.
 */
export const useGlobalFilters = () => useFilterStore((state) => state.globalFilters)

/**
 * Get local filters for a specific page.
 */
export const useLocalFilters = (pageKey: string) =>
  useFilterStore((state) => state.localFilters[pageKey] || {})

/**
 * Get module filters for a specific module instance.
 */
export const useModuleFilters = (moduleId: string) =>
  useFilterStore((state) => state.moduleFilters[moduleId] || {})

/**
 * Get cached global filter values for dropdowns.
 */
export const useCachedGlobalValues = () =>
  useFilterStore((state) => state.cachedGlobalValues)

/**
 * Get the current page key.
 */
export const useCurrentPageKey = () =>
  useFilterStore((state) => state.currentPageKey)

/**
 * Check if there are any active filters.
 */
export const useHasActiveFilters = () => {
  const store = useFilterStore()
  return store.hasActiveFilters()
}

/**
 * Get active global filters (excluding null/empty).
 */
export const useActiveGlobalFilters = () => {
  const store = useFilterStore()
  return store.getActiveGlobalFilters()
}

/**
 * Get global filter configs from filter configuration.
 */
export const useGlobalFilterConfigs = () =>
  useFilterStore((state) => state.filterConfig?.globalFilters || [])

/**
 * Get saved filters.
 */
export const useSavedFilters = () =>
  useFilterStore((state) => state.savedFilters)

/**
 * Get compare mode state for a specific table instance.
 */
export const useCompareMode = (instanceId: string) =>
  useFilterStore((state) => state.compareModes[instanceId] ?? false)

/**
 * Get compare mode setter (per-table).
 */
export const useSetCompareMode = () =>
  useFilterStore((state) => state.setCompareMode)

/**
 * Get compare version state.
 */
export const useCompareVersion = () =>
  useFilterStore((state) => state.compareVersion)

/**
 * Get compare version setter.
 */
export const useSetCompareVersion = () =>
  useFilterStore((state) => state.setCompareVersion)

/**
 * Get SMQ filter state.
 */
export const useSmqFilter = () =>
  useFilterStore((state) => state.smqFilter)

/**
 * Get SMQ filter setter.
 */
export const useSetSmqFilter = () =>
  useFilterStore((state) => state.setSmqFilter)

/**
 * Get SMQ filter clear function.
 */
export const useClearSmqFilter = () =>
  useFilterStore((state) => state.clearSmqFilter)
